import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Brain } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "./components/BasicAdvancedWrapper";
import { MultiStepConfig } from "./components/MultiStepGradeModal";
import { CreateModalProps } from "./index";
import {
  DetectionMethodsStep,
  ReEvaluationMethodsStep,
} from "./misconceptions/steps";
import MisconceptionSpecificFields from "./misconceptions/MisconceptionSpecificFields";
import { CommonMisconceptionCreateRequestDto } from "@packages/api/madlenAPI.schemas";

interface MisconceptionAdvancedData
  extends CommonMisconceptionCreateRequestDto {
  detectionMethods?: string[];
  reEvaluationMethods?: string[];
  customContext?: string;

  assessmentConfiguration?: {
    focusAreas?: string[];
    includeDetectionStrategies?: boolean;
    includeRemediation?: boolean;
  };
}

interface MisconceptionCreateModalProps
  extends Omit<CreateModalProps, "onSubmit" | "themeColor"> {
  onSubmit: (input: any) => Promise<void>;
  themeColor?: string;
}

export const MisconceptionCreateModal: React.FC<
  MisconceptionCreateModalProps
> = ({ onSubmit, initialInput, ...props }) => {
  const { t } = useTranslation();

  // State for basic misconception fields
  const [basicContext, setBasicContext] = useState(
    (initialInput as any).context || "",
  );

  // Define the initial data structure for advanced mode
  const initialAdvancedData: MisconceptionAdvancedData = {
    // Basic CreateModal data (will be populated from first step)
    courseName: initialInput.courseName as any,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType: initialInput.generationType as any,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage: initialInput.outputLanguage as any,

    detectionMethods: [],
    reEvaluationMethods: [],
    customContext: basicContext,

    assessmentConfiguration: {
      focusAreas: [],
      includeDetectionStrategies: false,
      includeRemediation: false,
    },
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<MisconceptionAdvancedData>[] = [
    {
      id: "detection-methods",
      title: t("misconceptions.steps.detection.title"),
      description: t("misconceptions.steps.detection.description"),
      icon: <AlertCircle size={16} />,
      component: DetectionMethodsStep,
      validation: (data) => {
        // At least one detection method should be selected
        return !!(data.detectionMethods && data.detectionMethods.length > 0);
      },
    },
    {
      id: "re-evaluation-methods",
      title: t("misconceptions.steps.reevaluation.title"),
      description: t("misconceptions.steps.reevaluation.description"),
      icon: <Brain size={16} />,
      component: ReEvaluationMethodsStep,
      validation: (data) => {
        return !!(
          data.reEvaluationMethods && data.reEvaluationMethods.length > 0
        );
      },
    },
  ];

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    let submissionData = { ...data };

    // Combine context with prompt if context is provided
    if (basicContext.trim()) {
      submissionData.prompt = `${basicContext.trim()}\n\n${data.prompt}`;
    }

    await onSubmit(submissionData);
  };

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (data: MisconceptionAdvancedData) => {
    let submissionData = { ...data };

    // Construct enriched prompt based on advanced settings
    let enrichedPrompt = data.prompt || "";

    // Add context if provided from basic context state
    if (basicContext?.trim()) {
      enrichedPrompt = `${basicContext.trim()}\n\n${enrichedPrompt}`;
    }

    // Add detection methods
    if (data.detectionMethods && data.detectionMethods.length > 0) {
      enrichedPrompt += `\n\nPreferred Detection Methods: ${data.detectionMethods.join(", ")}`;
    }

    // Add re-evaluation methods
    if (data.reEvaluationMethods && data.reEvaluationMethods.length > 0) {
      enrichedPrompt += `\nPreferred Re-evaluation Methods: ${data.reEvaluationMethods.join(", ")}`;
    }

    // Add assessment configuration
    if (data.assessmentConfiguration?.includeDetectionStrategies) {
      enrichedPrompt += `\nInclude detailed detection strategies in the response.`;
    }

    if (data.assessmentConfiguration?.includeRemediation) {
      enrichedPrompt += `\nInclude remediation techniques and interventions.`;
    }

    // Add focus areas
    if (
      data.assessmentConfiguration?.focusAreas &&
      data.assessmentConfiguration.focusAreas.length > 0
    ) {
      enrichedPrompt += `\nFocus Areas: ${data.assessmentConfiguration.focusAreas.join(", ")}`;
    }

    // Add instruction for structured output
    enrichedPrompt += `\n\nPlease provide assessment strategies with detection_methods and re_evaluation_methods in the response.`;

    // Clean up the advanced-specific fields before submission
    const {
      detectionMethods,
      reEvaluationMethods,
      assessmentConfiguration,
      customContext,
      ...cleanedData
    } = data;
    cleanedData.prompt = enrichedPrompt;

    await onSubmit(cleanedData);
  };

  // Handle context change from basic mode
  const handleContextChange = (context: string) => {
    setBasicContext(context);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<MisconceptionAdvancedData> = {
    createModalProps: {
      ...props,
      initialInput,
      themeColor: "red",
      onSubmit: handleBasicSubmit,
      backPath: "/content/misconceptions",
      submitButtonText: t("common.generate"),
      after: (
        <MisconceptionSpecificFields
          context={basicContext}
          onChange={handleContextChange}
          themeColor="red"
        />
      ),
    },
    multiStepProps: {
      steps: advancedSteps,
      initialData: initialAdvancedData,
      title: t(
        "quickActions.misconceptionCreate",
        "Create Common Misconceptions",
      ),
      themeColor: "red",
      backPath: "/content/misconceptions",
      isLoading: props.isLoading,
      error: props.error,
      loadingTitle: props.loadingTitle,
      errorTitle: props.errorTitle,
      errorDescription: props.errorDescription,
      onFinalSubmit: handleAdvancedSubmit,
      basicModeSubmitText: t("common.generate"),
      advancedModeSubmitText: t(
        "misconceptions.createAdvanced",
        "Create Advanced Misconceptions Analysis",
      ),
    },
    themeColor: "red",
    title: t(
      "quickActions.misconceptionCreate",
      "Create Common Misconceptions",
    ),
    backPath: "/content/misconceptions",
    enableAdvancedMode: true,
    defaultMode: "basic",
    showModeToggle: true,
    onBasicSubmit: handleBasicSubmit,
    onAdvancedSubmit: handleAdvancedSubmit,
  };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default MisconceptionCreateModal;
